import React, { useState } from 'react';
import { BoltIcon, EnvelopeIcon, ArrowLeftIcon, CheckIcon } from '@patternfly/react-icons';
import { forgotPassword } from '@app/utils/utils';
import { BorderedInput } from '@app/Login/BorderedInput';

const PURPLE_GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)';

const ForgotPasswordPage: React.FunctionComponent = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setIsLoading(true);
        try {
            await forgotPassword(email);
        } catch (error) {
            // silently fail — always show success to avoid email enumeration
        } finally {
            setIsLoading(false);
            setSubmitted(true);
        }
    };

    const styles = {
        page: {
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
            padding: '24px'
        },
        card: {
            background: '#ffffff',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '460px',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)',
            padding: '40px 40px 32px'
        },
        logoBadge: {
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)'
        },
        title: {
            color: '#0f172a',
            fontSize: '28px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '8px',
            textAlign: 'center' as const,
            letterSpacing: '-0.5px'
        },
        subtitle: {
            color: '#6b7280',
            fontSize: '15px',
            margin: 0,
            marginBottom: '28px',
            textAlign: 'center' as const,
            lineHeight: 1.5
        },
        fieldLabel: {
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '8px'
        },
        fieldGroup: {
            marginBottom: '20px'
        },
        button: {
            width: '100%',
            background: PURPLE_GRADIENT,
            color: '#ffffff',
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            cursor: 'pointer'
        },
        buttonDisabled: {
            width: '100%',
            background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: 'none',
            cursor: 'not-allowed'
        },
        backLinkRow: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px'
        },
        backLink: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: '#6366f1',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '14px'
        },
        successIconWrap: {
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: '#059669',
            fontSize: '32px'
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                {submitted ? (
                    <>
                        <div style={styles.successIconWrap}>
                            <CheckIcon />
                        </div>
                        <h1 style={styles.title}>Check your email</h1>
                        <p style={styles.subtitle}>We sent a reset link to your inbox</p>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '6px' }}>
                                If an account exists for
                            </p>
                            <p style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', marginBottom: '20px' }}>
                                {email}
                            </p>
                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', lineHeight: 1.6 }}>
                                you'll receive a password reset link shortly. The link expires in 1 hour.
                            </p>
                        </div>
                        <div style={styles.backLinkRow}>
                            <a href="/" style={styles.backLink}>
                                <ArrowLeftIcon /> Back to login
                            </a>
                        </div>
                    </>
                ) : (
                    <>
                        <div style={styles.logoBadge}>
                            <BoltIcon style={{ fontSize: '28px', color: '#fbbf24' }} />
                        </div>
                        <h1 style={styles.title}>Forgot password?</h1>
                        <p style={styles.subtitle}>
                            Enter your email and we'll send you a link to reset your password.
                        </p>

                        <form onSubmit={handleSubmit} style={{ border: 'none', padding: 0, background: 'transparent', margin: 0, display: 'block', maxWidth: 'none' }}>
                            <div style={styles.fieldGroup}>
                                <label style={styles.fieldLabel} htmlFor="forgot-email">Email</label>
                                <BorderedInput
                                    id="forgot-email"
                                    value={email}
                                    onChange={setEmail}
                                    type="email"
                                    placeholder="you@example.com"
                                    icon={<EnvelopeIcon />}
                                    autoComplete="email"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={!email || isLoading}
                                style={!email || isLoading ? styles.buttonDisabled : styles.button}
                            >
                                {isLoading ? 'Sending...' : 'Send reset link'}
                            </button>
                            <div style={styles.backLinkRow}>
                                <a href="/" style={styles.backLink}>
                                    <ArrowLeftIcon /> Back to login
                                </a>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export { ForgotPasswordPage };
